import AsyncStorage from "@react-native-async-storage/async-storage";

import { safeWebsite } from "../donations/fundraiserIntent";

// A funeral home arriving from eventlive.pro/funeral-streaming, e.g.
// /signin?account_type=business&broadcaster_role=funeralhome&company_name=…
//   &company_url=…&email=…&first_name=…&last_name=…&funeral_lead=<id>
//   &redeem=3free&utm_…
// Navigation drops the query string before signup (/signin → /onboarding →
// /signin), which is how these visitors used to lose their 3 free services:
// the intent keeps the landing params until the account is created, so the
// signup metadata carries `redeem` and `funeral_lead` to the sign-up Lambda
// (eventlive-cdk/lambda/funeralTrial.ts verifies the lead and adds the
// credits). Afterwards it drives the dashboard welcome until dismissed.
export const FUNERAL_INTENT_KEY = "pendingFuneralHomeIntent";
export const FUNERAL_INTENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const FUNERAL_TRIAL_CREDITS = 3;

const VERSION = 1;
// Answers OnboardingNew keeps between steps (see OnboardingNew.js).
const ONBOARDING_DATA_KEY = "onboardingData";

// Landing params kept for the signup metadata. Nothing else from the link is
// trusted: in particular no credit amounts.
const PASSTHROUGH_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
];

let cachedIntent; // undefined = not read yet, null = none

const clean = (value, max = 200) => {
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === "string" ? first.trim().slice(0, max) : "";
};

const isLeadId = (value) => /^[A-Za-z0-9_-]{1,80}$/.test(value);
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const parseFuneralHomeIntent = (params, now = Date.now()) => {
  if (!params || typeof params !== "object") return null;
  const get = (key, max) => clean(params[key], max);

  const leadId = get("funeral_lead", 80);
  if (get("broadcaster_role", 40) !== "funeralhome" || !isLeadId(leadId)) {
    return null;
  }

  const companyName = get("company_name", 120);
  const companyUrl = safeWebsite(get("company_url", 200));
  const redeem = get("redeem", 20) === "3free" ? "3free" : "";

  const metadata = {
    account_type: "business",
    broadcaster_role: "funeralhome",
    funeral_lead: leadId,
  };
  if (companyName) metadata.company_name = companyName;
  if (companyUrl) metadata.company_url = companyUrl;
  if (redeem) metadata.redeem = redeem;
  for (const key of PASSTHROUGH_KEYS) {
    const value = get(key, 200);
    if (value) metadata[key] = value;
  }

  const email = get("email", 254);
  return {
    version: VERSION,
    createdAt: now,
    leadId,
    companyName,
    companyUrl,
    email: isEmail(email) ? email : "",
    firstName: get("first_name", 100),
    lastName: get("last_name", 100),
    trial: !!redeem,
    metadata,
    // The dashboard welcome is shown until dismissed or a service is scheduled.
    dismissed: false,
  };
};

const isValidIntent = (intent, now = Date.now()) =>
  !!intent &&
  typeof intent === "object" &&
  intent.version === VERSION &&
  Number.isFinite(intent.createdAt) &&
  now - intent.createdAt <= FUNERAL_INTENT_MAX_AGE_MS;

const persist = async (intent) => {
  cachedIntent = intent || null;
  try {
    if (intent) {
      await AsyncStorage.setItem(FUNERAL_INTENT_KEY, JSON.stringify(intent));
    } else {
      await AsyncStorage.removeItem(FUNERAL_INTENT_KEY);
    }
  } catch (error) {
    // Storage can be unavailable (private mode); the in-memory copy still
    // carries the intent through this session.
  }
  return cachedIntent;
};

// Synchronous read of what this session already knows (no storage access).
export const peekFuneralHomeIntent = () =>
  isValidIntent(cachedIntent) ? cachedIntent : null;

export const readFuneralHomeIntent = async () => {
  if (cachedIntent !== undefined) {
    if (isValidIntent(cachedIntent)) return cachedIntent;
    if (cachedIntent) await persist(null);
    return null;
  }
  let parsed = null;
  try {
    const raw = await AsyncStorage.getItem(FUNERAL_INTENT_KEY);
    parsed = raw ? JSON.parse(raw) : null;
  } catch (error) {
    parsed = null;
  }
  // Another read or capture may have finished while storage was loading.
  if (cachedIntent !== undefined) return peekFuneralHomeIntent();
  if (isValidIntent(parsed)) {
    cachedIntent = parsed;
    return parsed;
  }
  await persist(null);
  return null;
};

// `freshVisit`: the app was just opened from the landing page link (web).
export const captureFuneralHomeIntent = async (
  params,
  { freshVisit = false } = {}
) => {
  const parsed = parseFuneralHomeIntent(params);
  if (!parsed) return null;
  const existing = await readFuneralHomeIntent();
  // Reload or back button with the same link: keep the dismissed state.
  if (existing && existing.leadId === parsed.leadId) {
    return persist({ ...parsed, dismissed: existing.dismissed });
  }
  // A new landing-page visit starts onboarding afresh: answers left over from
  // an abandoned onboarding on this device (e.g. a wedding title and link)
  // must not end up in this funeral home's signup and create a stray event.
  if (freshVisit) {
    try {
      await AsyncStorage.removeItem(ONBOARDING_DATA_KEY);
    } catch (error) {
      // Nothing stored or storage unavailable.
    }
  }
  return persist(parsed);
};

export const captureFuneralHomeIntentFromUrl = (search) => {
  try {
    const params = Object.fromEntries(new URLSearchParams(search || ""));
    if (!parseFuneralHomeIntent(params)) return null;
    return captureFuneralHomeIntent(params, { freshVisit: true });
  } catch (error) {
    return null;
  }
};

export const updateFuneralHomeIntent = async (patch) => {
  const intent = await readFuneralHomeIntent();
  if (!intent) return null;
  return persist({ ...intent, ...patch });
};

export const clearFuneralHomeIntent = async () => {
  await persist(null);
};

export const funeralSignupMetadata = (intent) =>
  intent?.metadata && typeof intent.metadata === "object"
    ? { ...intent.metadata }
    : {};

// Test helper: forget the in-memory copy so the next read hits storage.
export const __resetFuneralHomeIntentCache = () => {
  cachedIntent = undefined;
};
